import { addDays, isCivilDate } from './date.js';
/**
 * Parses the kind of German a person says at a fridge, into a civil date.
 *
 * Deliberately a small hand-written grammar rather than a general NLP model:
 * the utterance space here is tiny, and a rule set is debuggable, instant and
 * offline. Whatever it returns still passes through the "Stimmt das so?"
 * confirm screen, so a miss is a correction rather than bad data.
 *
 * Note Whisper normalises numbers aggressively — "fünfundzwanzigster August"
 * usually comes back as "25. August". Both forms are handled.
 */
const MONTHS = {
    januar: 1, jaenner: 1, februar: 2, maerz: 3, marz: 3, april: 4, mai: 5, juni: 6,
    juli: 7, august: 8, september: 9, oktober: 10, november: 11, dezember: 12,
};
const ONES = {
    ein: 1, eins: 1, eine: 1, zwei: 2, drei: 3, vier: 4, fuenf: 5, sechs: 6, sieben: 7,
    acht: 8, neun: 9, zehn: 10, elf: 11, zwoelf: 12, dreizehn: 13, vierzehn: 14,
    fuenfzehn: 15, sechzehn: 16, siebzehn: 17, achtzehn: 18, neunzehn: 19, zwanzig: 20,
    dreissig: 30,
};
const WEEKDAYS = {
    sonntag: 0, montag: 1, dienstag: 2, mittwoch: 3, donnerstag: 4, freitag: 5, samstag: 6, sonnabend: 6,
};
/** Strips umlauts/ß so the tables above can stay ASCII. */
function fold(s) {
    return s
        .toLowerCase()
        .replaceAll('ä', 'ae').replaceAll('ö', 'oe').replaceAll('ü', 'ue').replaceAll('ß', 'ss')
        .replace(/[„“"',]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** "fuenfundzwanzigster" -> 25, "dritter" -> 3, "zwanzigsten" -> 20. */
function ordinalWord(word) {
    const w = word.replace(/(ster|sten|stem|ste|ter|ten|tem|te)$/, '');
    // 'erster' loses 'ster' and lands on 'er'; 'ersten' loses 'sten' -> 'er' too.
    const irregular = { er: 1, ers: 1, drit: 3, dritt: 3, sieb: 7, ach: 8 };
    if (irregular[w] !== undefined)
        return irregular[w];
    const und = w.match(/^(.+?)und(zwanzig|dreissig)$/);
    if (und) {
        const one = ONES[und[1]];
        const ten = ONES[und[2]];
        if (one !== undefined && ten !== undefined)
            return ten + one;
    }
    if (ONES[w] !== undefined)
        return ONES[w];
    // "einunddreissig" style already handled; try bare tens like "dreissig".
    return null;
}
function cardinalWord(word) {
    if (/^\d+$/.test(word))
        return Number(word);
    if (ONES[word] !== undefined)
        return ONES[word];
    const und = word.match(/^(.+?)und(zwanzig|dreissig)$/);
    if (und) {
        const one = ONES[und[1]];
        const ten = ONES[und[2]];
        if (one !== undefined && ten !== undefined)
            return ten + one;
    }
    return null;
}
function pad(n) {
    return String(n).padStart(2, '0');
}
/** Picks the next occurrence of day/month at or after `today`. */
function resolveDayMonth(day, month, year, today) {
    const currentYear = Number(today.slice(0, 4));
    const candidateYear = year ?? currentYear;
    const built = `${candidateYear}-${pad(month)}-${pad(day)}`;
    if (!isCivilDate(built))
        return null; // rejects 31.02. and friends
    if (year === null && built < today) {
        const next = `${currentYear + 1}-${pad(month)}-${pad(day)}`;
        return isCivilDate(next) ? next : null;
    }
    return built;
}
export function parseGermanDate(input, today) {
    const text = fold(input);
    if (!text)
        return null;
    // 25.08.2026 / 25.8.26 / 25.08.
    const numeric = text.match(/(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{2,4})?/);
    if (numeric) {
        const day = Number(numeric[1]);
        const month = Number(numeric[2]);
        const rawYear = numeric[3];
        let year = null;
        if (rawYear)
            year = rawYear.length === 2 ? 2000 + Number(rawYear) : Number(rawYear);
        const resolved = resolveDayMonth(day, month, year, today);
        if (resolved)
            return { date: resolved, rule: 'numeric' };
    }
    const words = text.split(' ');
    // "25. August" / "fuenfundzwanzigster august"
    for (let i = 0; i < words.length; i++) {
        const monthName = words[i].replace(/\.$/, '');
        const month = MONTHS[monthName];
        if (month === undefined)
            continue;
        for (let back = 1; back <= 2 && i - back >= 0; back++) {
            const token = words[i - back].replace(/\.$/, '');
            const day = /^\d{1,2}$/.test(token) ? Number(token) : ordinalWord(token);
            if (day === null || day < 1 || day > 31)
                continue;
            const yearToken = words[i + 1]?.replace(/\.$/, '');
            const year = yearToken && /^\d{4}$/.test(yearToken) ? Number(yearToken) : null;
            const resolved = resolveDayMonth(day, month, year, today);
            if (resolved)
                return { date: resolved, rule: 'day-month' };
        }
    }
    // "in drei tagen" / "in 10 tagen"
    const relDays = text.match(/in\s+(\S+)\s+tag(?:en|e)?/);
    if (relDays) {
        const n = cardinalWord(relDays[1]);
        if (n !== null)
            return { date: addDays(today, n), rule: 'relative-days' };
    }
    // "in zwei wochen"
    const relWeeks = text.match(/in\s+(\S+)\s+woche(?:n)?/);
    if (relWeeks) {
        const n = cardinalWord(relWeeks[1]);
        if (n !== null)
            return { date: addDays(today, n * 7), rule: 'relative-weeks' };
    }
    if (/\buebermorgen\b/.test(text))
        return { date: addDays(today, 2), rule: 'named-day' };
    if (/\bmorgen\b/.test(text))
        return { date: addDays(today, 1), rule: 'named-day' };
    if (/\bheute\b/.test(text))
        return { date: today, rule: 'named-day' };
    // "naechsten freitag" / bare "freitag" -> next such weekday, never today
    for (const [name, dow] of Object.entries(WEEKDAYS)) {
        if (!new RegExp(`\\b${name}\\b`).test(text))
            continue;
        const todayDow = new Date(today + 'T00:00:00Z').getUTCDay();
        let delta = (dow - todayDow + 7) % 7;
        if (delta === 0)
            delta = 7;
        if (/\bnaechste[nr]?\b/.test(text) && delta < 7)
            delta += 0;
        return { date: addDays(today, delta), rule: 'weekday' };
    }
    return null;
}
